import { Text } from "@/components/ui/baseUi/text"
import type { ProgramCalendarEventType } from "@/modules/calendar/calendarTypes"
import { cn } from "@/modules/lib/utils"
import { NoteForAthletesInEvent } from "@/modules/programs/components/programSession/blocks/notes/noteForAthletesInEvent"
import { NoteForCoachesInEvent } from "@/modules/programs/components/programSession/blocks/notes/noteForCoachesInEvent"
import { ProgramCalendarEventRestDay } from "@/modules/programs/components/programSession/programCalendarEventRestDay"
import { ProgramCalendarSessionBlockContainer } from "@/modules/programs/components/programSession/programCalendarSessionBlockContainer"
import { ProgramCalendarSessionFooterContainer } from "@/modules/programs/components/programSession/programCalendarSessionFooter"
import { ProgramCalendarSessionHeaderContainer } from "@/modules/programs/components/programSession/programCalendarSessionHeader"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import { useDetailedProgramSessionInvalidate } from "@/modules/sessions/hooks/useProgramSessions"
import { getDisplayExerciseCategory } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/exerciseUtils"
import { MediaDetailsInEvent } from "@/modules/sessions/sessionDetails/blocks/mediaBlock/components/mediaDetailsInEvent"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { CardioSelectedIcon } from "@images/svgIcons/cardioIcon"
import { ChronoIcon } from "@images/svgIcons/chronoIcon"
import { GymSelectedIcon } from "@images/svgIcons/gymIcon"
import { WeightliftingSelectedIcon } from "@images/svgIcons/weightliftingIcon"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { displayTotalTime, getTrainingChronoTitle } from "@modules/chrono/chronoUtils"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useSessionBlockSort } from "@modules/programs/hooks/useSessionBlockSort"
import { type DetailedSessionType } from "@modules/sessions/sessionsType"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { EyeOff, LoaderCircle } from "lucide-react"
import { useEffect, useMemo, useRef } from "react"
import styled from "styled-components"

type ProgramCalendarEventProps = {
  index: number
  event: ProgramCalendarEventType
  session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity
  displayDetailedEvents?: boolean
  onEventClick?: (arg: DetailedSessionType) => Promise<void>
  onBlockEditClick: (session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity, orderIndex: number) => void
  onCreateBlockClick: (
    session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity,
    newBlockType: SessionBlockType,
  ) => void
  onImportTemplateClick: (session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity) => void
  isUpdating?: boolean
  isDetailsOpen?: boolean
  programId: string
} & React.ComponentProps<"div">

export function ProgramCalendarEvent({
  displayDetailedEvents = false,
  event,
  session,
  onEventClick,
  onBlockEditClick,
  onCreateBlockClick,
  onImportTemplateClick,
  isUpdating = false,
  isDetailsOpen = false,
  programId,
  className,
  ...props
}: ProgramCalendarEventProps) {
  const { setDraggingEvent, setIsDragging } = useCalendar()
  const programCalendarEventRef = useRef<HTMLDivElement>(null)
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(programId)
  const { data: program } = useProgram(programId)
  const { isSidebarVisible } = useSidebar()
  const { moveBlockMutation } = useSessionBlockSort()
  const { invalidateQuerySession } = useDetailedProgramSessionInvalidate(event.id)

  useEffect(() => {
    const programCalendarEvent = programCalendarEventRef.current
    const dragstart = () => {
      setIsDragging(true)
      setDraggingEvent(event)
    }

    programCalendarEvent?.addEventListener("dragstart", dragstart)
    return () => {
      programCalendarEvent?.removeEventListener("dragstart", dragstart)
    }
  }, [event, setDraggingEvent, setIsDragging])

  const hideSessionContent = useMemo(() => {
    const sessionHasStarted = "date" in event && event.date && new Date() > new Date(event.date)

    return sessionHasStarted ? false : event.hideSessionContent
  }, [event])

  const canSeeActions = useMemo(
    () => (isAuthor || isAcceptedSharedCoach) && !isSidebarVisible,
    [isAuthor, isAcceptedSharedCoach, isSidebarVisible],
  )

  const visibleBlocks = useMemo(() => event.blocks, [event.blocks])
  const firstBlockIndex = visibleBlocks[0]?.content.orderIndex ?? 0
  const lastBlockIndex = visibleBlocks[visibleBlocks.length - 1]?.content.orderIndex ?? visibleBlocks.length - 1

  const onMoveBlock = (direction: "UP" | "DOWN", orderIndex: number) => {
    if (direction === "UP" && orderIndex === firstBlockIndex) return
    if (direction === "DOWN" && orderIndex === lastBlockIndex) return

    const currentBlockIndex = visibleBlocks.findIndex((block) => block.content.orderIndex === orderIndex)

    const newBlockIndex =
      direction === "UP"
        ? visibleBlocks[currentBlockIndex - 1]?.content.orderIndex
        : visibleBlocks[currentBlockIndex + 1]?.content.orderIndex

    if (newBlockIndex === undefined) return

    void moveBlockMutation({ sessionId: event.id, blockIndex: orderIndex, newBlockIndex }).then(() => {
      invalidateQuerySession()
    })
  }

  const handleSeeSessionClick = async () => {
    if (!onEventClick) return
    await onEventClick({
      id: event.id,
      programId,
      programType: program.type,
      date: "date" in event ? event.date : null,
    })
  }

  const canUserSeeSessionContent = !hideSessionContent || isAuthor || isAcceptedSharedCoach
  const shouldDisplayTags =
    (event.cardioTag || event.gymnasticTag || event.weightliftingTag) && canUserSeeSessionContent

  return (
    <div key={event.id} id={event.id} ref={programCalendarEventRef} className="border-b pb-2">
      <div
        className={cn(
          "transition-in gap-2 rounded-3xl bg-linear-to-b from-primary-100 from-0% via-primary-100 via-[50px] to-[100px] p-0 pb-2 transition-colors duration-200 has-[#session-header:hover]:to-primary-100 has-[.session-content-stack:hover]:from-transparent has-[.session-content-stack:hover]:via-transparent has-[.session-content-stack:hover]:to-transparent",
          {
            "cursor-pointer": !!onEventClick,
            "to-primary-100": isDetailsOpen,
            "to-surface-background": !isDetailsOpen,
            "bg-neutral-100": "restDay" in event && event.restDay,
          },
          className,
          props,
        )}
        onClick={() => void handleSeeSessionClick()}
      >
        {"restDay" in event && event.restDay ? (
          <ProgramCalendarEventRestDay
            programName={program.name}
            dayIndex={event.dayIndex}
            duration={program.duration ?? 0}
          />
        ) : (
          <>
            <ProgramCalendarSessionHeaderContainer
              onSeeSessionClick={() => void handleSeeSessionClick()}
              session={session}
              canSeeActions={canSeeActions}
              onCreateBlockClick={onCreateBlockClick}
              onImportTemplateClick={onImportTemplateClick}
            >
              <div className="flex flex-row items-center justify-between p-2">
                <Text fontStyle="TM" className="font-bold text-textDefault-body" data-event-title>
                  {event.title}
                </Text>

                {isUpdating && <LoaderCircle className="h-4 w-4 animate-spin text-green-300" />}
                {hideSessionContent && <EyeOff className="text-green-300" size={16} />}
              </div>

              {shouldDisplayTags && (
                <div className="flex flex-row items-center justify-start gap-2 px-2">
                  {event.gymnasticTag && <GymSelectedIcon color="" width={20} height={20} />}
                  {event.weightliftingTag && <WeightliftingSelectedIcon color="" width={20} height={20} />}
                  {event.cardioTag && <CardioSelectedIcon color="" width={20} height={20} />}
                </div>
              )}
            </ProgramCalendarSessionHeaderContainer>

            {visibleBlocks.length > 0 && (
              <SessionBlockList $blur={!canUserSeeSessionContent} className="session-content-stack">
                {!canUserSeeSessionContent && <EyeOff className="text-green-300" size={16} />}
                {visibleBlocks.map((block, blockIndex) => (
                  <ProgramCalendarSessionBlockContainer
                    key={blockIndex}
                    canSeeActions={canSeeActions}
                    orderIndex={block.content.orderIndex}
                    firstBlockIndex={firstBlockIndex}
                    lastBlockIndex={lastBlockIndex}
                    session={session}
                    onMoveBlock={onMoveBlock}
                    onBlockEditClick={onBlockEditClick}
                  >
                    {block.type === SessionBlockType.SESSION_EXERCISE ? (
                      <div className="flex flex-col gap-2" key={blockIndex}>
                        <Text fontStyle="TS" className="font-sora text-base">
                          {getDisplayExerciseCategory(block.content.category, block.content.otherCategory)}
                        </Text>

                        {block.content.chrono?.type && (
                          <div className="flex items-center">
                            <ChronoIcon color="currentColor" className="text-textDefault-body" width={14} height={14} />
                            <div className="flex flex-col gap-0">
                              <Text fontStyle="LS" className="ml-2 text-textDefault-body">
                                {getTrainingChronoTitle(block.content.chrono.type)}
                              </Text>
                              <Text fontStyle="LS" className="ml-2 text-textDefault-body">
                                {displayTotalTime(block.content.chrono)}
                              </Text>
                            </div>
                          </div>
                        )}
                        <HURichTextView
                          value={formatTextToRichText(block.content.description)}
                          paragrapheFontStyle="BS"
                          className={cn(
                            displayDetailedEvents ? "" : "line-clamp-3 wrap-break-word whitespace-break-spaces",
                          )}
                        />
                      </div>
                    ) : block.type === SessionBlockType.COACH_NOTE ? (
                      <NoteForCoachesInEvent
                        key={blockIndex}
                        note={block.content}
                        displayDetailedEvents={displayDetailedEvents}
                      />
                    ) : block.type === SessionBlockType.ATHLETE_NOTE ? (
                      <NoteForAthletesInEvent
                        key={blockIndex}
                        note={block.content}
                        displayDetailedEvents={displayDetailedEvents}
                      />
                    ) : (
                      /*block.type === SessionBlockType.MEDIAS ? */
                      <MediaDetailsInEvent
                        key={blockIndex}
                        medias={block.content.medias}
                        displayDetailedEvents={displayDetailedEvents}
                      />
                    )}
                  </ProgramCalendarSessionBlockContainer>
                ))}
              </SessionBlockList>
            )}
            <ProgramCalendarSessionFooterContainer
              session={session}
              className={canSeeActions ? "" : "opacity-0"}
              onCreateBlockClick={onCreateBlockClick}
              onImportTemplateClick={onImportTemplateClick}
            />
          </>
        )}
      </div>
    </div>
  )
}

const SessionBlockList = styled.div<{ $blur: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
  margin-top: 0.5rem;

  &::before {
    display: ${({ $blur }) => ($blur ? "block" : "none")};
    content: "";
    position: absolute;
    border-radius: 20px;
    margin: -5px;
    top: 0;
    left: 0;
    width: 105%;
    height: 105%;
    background: rgba(255, 255, 255, 0.23);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    z-index: 2;
  }
`
